import { FlagFilled } from '@griply/assets';
import { Goal, initialDateFormat, Timestamp } from '@griply/types';
import { formatFullDate, getWeekStartsOn } from '@griply/utils';
import { differenceInDays, isBefore, isSameDay } from 'date-fns';
import { t } from 'i18next';
import React, { useMemo, useRef } from 'react';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import { TimeLeft } from 'shared/components/ui/time-left';
import { Timeframe } from 'shared/components/ui/timeframe';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { useTheme } from 'styled-components';

import * as Styled from './goal-list-card.style';

export type DeadlineCellProps = {
  goal: Goal;
};

export const DeadlineCell: React.FC<DeadlineCellProps> = ({ goal }) => {
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const deadlineRef = useRef<HTMLSpanElement>(null);
  const deadlineButtonRef = useRef<HTMLSpanElement>(null);
  const {
    menuOpen: deadlineMenuOpen,
    openMenu: openDeadlineMenu,
    closeMenu: closeDeadlineMenu,
  } = useOpenMenu();

  const started =
    !goal.startDate ||
    isBefore(goal.startDate, today) ||
    isSameDay(goal.startDate, today);

  const deadlineOverdue = useMemo(
    () =>
      !!goal.deadline && !goal.completedAt && isBefore(goal.deadline, today),
    [goal.deadline, goal.completedAt, today],
  );

  const deadlineUrgent = useMemo(
    () =>
      !!goal.deadline &&
      !goal.completedAt &&
      !deadlineOverdue &&
      differenceInDays(goal.deadline, today) < 8,
    [goal.deadline, goal.completedAt, today, deadlineOverdue],
  );

  const deadlineColor = deadlineOverdue
    ? theme.palette.error.main
    : deadlineUrgent
      ? theme.palette.warning.main
      : !started
        ? theme.palette.text.secondary
        : theme.palette.text.primary;

  const deadlineIconColor =
    deadlineOverdue || deadlineUrgent ? deadlineColor : theme.palette.grey[600];

  const onDeadlineClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openDeadlineMenu();
  };

  const onDeadlineChange = (date?: Timestamp) => {
    updateGoal({ goal: { id: goal.id, deadline: date ?? null } });
    closeDeadlineMenu();
  };

  return (
    <>
      <Styled.DataContainer
        ref={deadlineRef}
        $type={Styled.DataTypes.Deadline}
        $isFrozen={!!goal.frozenAt}
        $isClickable={!goal.frozenAt}
        onClick={!goal.frozenAt ? onDeadlineClick : undefined}
      >
        {goal.deadline ? (
          <Styled.DataButton ref={deadlineButtonRef} $color={deadlineColor}>
            {!goal.completedAt ? (
              <>
                <Icon icon={FlagFilled} size={13} color={deadlineIconColor} />
                <TimeLeft date={goal.deadline} isShort />
              </>
            ) : (
              <Timeframe endDate={goal.deadline} isShort />
            )}
          </Styled.DataButton>
        ) : (
          <Styled.SelectPlaceholder $forceShow={deadlineMenuOpen}>
            {t('general.labels.select')}
          </Styled.SelectPlaceholder>
        )}
      </Styled.DataContainer>

      {!!goal.deadline && (
        <Tooltip
          referenceElement={deadlineButtonRef}
          label={formatFullDate(goal.deadline, dateFormat)}
        />
      )}

      {deadlineMenuOpen && (
        <DatepickerWithOptions
          referenceElement={deadlineRef}
          value={goal.deadline ?? undefined}
          onChange={onDeadlineChange}
          dateFormat={dateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.deadline}
          onOutside={closeDeadlineMenu}
        />
      )}
    </>
  );
};
